import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  createOverlayTemplate,
  createOverlayTemplateFolder,
  deleteOverlayTemplate,
  deleteOverlayTemplateFolder,
  getOverlayTemplate,
  getOverlayTemplateScopeKey,
  listOverlayTemplateFolders,
  listOverlayTemplates,
  listOverlayTemplatesPage,
  OverlayTemplateApiScope,
  OverlayTemplateDetailDto,
  OverlayTemplateFolderDto,
  OverlayTemplateListParams,
  OverlayTemplateQuotaDto,
  OverlayTemplateSummaryDto,
  OverlayTemplateWritePayload,
  updateOverlayTemplate,
  updateOverlayTemplateFolder,
  uploadOverlayTemplatePreview as uploadOverlayTemplatePreviewFile,
  WritableOverlayTemplateApiScope
} from '@/api/overlayTemplates';

export interface OverlayTemplateRequestError {
  message: string;
  fieldErrors: Record<string, unknown>;
}

export interface OverlayTemplatesState {
  templates: OverlayTemplateSummaryDto[];
  folders: OverlayTemplateFolderDto[];
  count: number;
  /**
   * What the plan allows the *writable* library to hold — which is not what
   * `count` measures: `count` follows the current filter and, in a profile
   * library, includes the shared account overlays that library does not own.
   *
   * Null while nothing has been loaded, and on a backend that does not report
   * a quota.
   */
  quota: OverlayTemplateQuotaDto | null;
  /**
   * Which library {@link OverlayTemplatesState.quota} describes, so a filter
   * change within one library keeps it while switching libraries drops it.
   */
  quotaScopeKey: string | null;
  next: string | null;
  previous: string | null;
  activeTemplate: OverlayTemplateDetailDto | null;
  loadingLibrary: boolean;
  libraryRequestId: string | null;
  activeLibraryQueryKey: string | null;
  loadingMoreTemplates: boolean;
  loadMoreRequestId: string | null;
  loadingMoreCursor: string | null;
  loadedTemplateCursors: string[];
  loadMoreError: string | null;
  loadingFolders: boolean;
  folderRequestId: string | null;
  folderError: string | null;
  activeFolderScopeKey: string | null;
  loadedLibraryScopeKey: string | null;
  folderNext: string | null;
  loadingMoreFolders: boolean;
  folderLoadMoreRequestId: string | null;
  loadingFolderCursor: string | null;
  loadedFolderCursors: string[];
  folderLoadMoreError: string | null;
  loadingTemplate: boolean;
  /** The detail request the editor is waiting on, so a superseded one that
   *  answers late cannot speak for the template now on screen. */
  templateRequestId: string | null;
  /** Why the open editor has no template. Separate from the library's
   *  `error`, which every other request writes to as well. */
  templateError: string | null;
  savingTemplate: boolean;
  movingTemplateUuid: string | null;
  deletingTemplateUuid: string | null;
  mutatingFolder: boolean;
  uploadingPreview: boolean;
  error: string | null;
  fieldErrors: Record<string, unknown>;
}

const initialState: OverlayTemplatesState = {
  templates: [],
  folders: [],
  count: 0,
  quota: null,
  quotaScopeKey: null,
  next: null,
  previous: null,
  activeTemplate: null,
  loadingLibrary: false,
  libraryRequestId: null,
  activeLibraryQueryKey: null,
  loadingMoreTemplates: false,
  loadMoreRequestId: null,
  loadingMoreCursor: null,
  loadedTemplateCursors: [],
  loadMoreError: null,
  loadingFolders: false,
  folderRequestId: null,
  folderError: null,
  activeFolderScopeKey: null,
  loadedLibraryScopeKey: null,
  folderNext: null,
  loadingMoreFolders: false,
  folderLoadMoreRequestId: null,
  loadingFolderCursor: null,
  loadedFolderCursors: [],
  folderLoadMoreError: null,
  loadingTemplate: false,
  templateRequestId: null,
  templateError: null,
  savingTemplate: false,
  movingTemplateUuid: null,
  deletingTemplateUuid: null,
  mutatingFolder: false,
  uploadingPreview: false,
  error: null,
  fieldErrors: {}
};

const toRequestError = (
  error: any,
  fallbackMessage: string
): OverlayTemplateRequestError => {
  const responseData = error?.response?.data;
  const message =
    typeof responseData?.detail === 'string'
      ? responseData.detail
      : fallbackMessage;
  const fieldErrors =
    responseData && typeof responseData === 'object' ? responseData : {};
  return { message, fieldErrors };
};

const sortFoldersByName = (
  folders: OverlayTemplateFolderDto[]
): OverlayTemplateFolderDto[] =>
  [...folders].sort((first, second) => {
    const nameComparison = first.name.localeCompare(second.name, undefined, {
      sensitivity: 'base'
    });
    return nameComparison || first.uuid.localeCompare(second.uuid);
  });

type OverlayTemplateThunkConfig = {
  rejectValue: OverlayTemplateRequestError;
};

export const getOverlayTemplateQueryKey = (
  params: OverlayTemplateListParams
): string =>
  JSON.stringify({
    scope: getOverlayTemplateScopeKey(params.scope),
    isShared: params.isShared ?? null,
    folderUuid: params.folderUuid ?? null,
    search: params.search?.trim() ?? '',
    ordering: params.ordering ?? '-pk'
  });

export const getOverlayTemplateFolderQueryKey = (
  scope: OverlayTemplateApiScope,
  isShared?: boolean
): string =>
  [
    getOverlayTemplateScopeKey(scope),
    isShared === undefined ? 'unfiltered' : String(isShared)
  ].join(':');

export const fetchOverlayTemplates = createAsyncThunk<
  Awaited<ReturnType<typeof listOverlayTemplates>>,
  OverlayTemplateListParams,
  OverlayTemplateThunkConfig
>('overlayTemplates/fetchTemplates', async (params, { rejectWithValue }) => {
  try {
    return await listOverlayTemplates(params);
  } catch (error: any) {
    return rejectWithValue(toRequestError(error, 'Failed to load overlays.'));
  }
});

export const fetchNextOverlayTemplates = createAsyncThunk<
  Awaited<ReturnType<typeof listOverlayTemplatesPage>>,
  { queryKey: string; nextUrl: string },
  OverlayTemplateThunkConfig & {
    state: { overlayTemplates: OverlayTemplatesState };
  }
>(
  'overlayTemplates/fetchNextTemplates',
  async ({ nextUrl }, { rejectWithValue }) => {
    try {
      return await listOverlayTemplatesPage(nextUrl);
    } catch (error: any) {
      return rejectWithValue(
        toRequestError(error, 'Failed to load more overlays.')
      );
    }
  },
  {
    condition: ({ queryKey, nextUrl }, { getState }) => {
      const state = getState().overlayTemplates;
      return (
        state.activeLibraryQueryKey === queryKey &&
        state.next === nextUrl &&
        !state.loadingMoreTemplates &&
        !state.loadedTemplateCursors.includes(nextUrl)
      );
    }
  }
);

export const fetchOverlayTemplateFolders = createAsyncThunk<
  Awaited<ReturnType<typeof listOverlayTemplateFolders>>,
  {
    scope: OverlayTemplateApiScope;
    isShared?: boolean;
    force?: boolean;
  },
  OverlayTemplateThunkConfig & {
    state: { overlayTemplates: OverlayTemplatesState };
  }
>(
  'overlayTemplates/fetchFolders',
  async ({ scope, isShared }, { rejectWithValue }) => {
    try {
      return await listOverlayTemplateFolders(scope, isShared);
    } catch (error: any) {
      return rejectWithValue(
        toRequestError(error, 'Failed to load overlay folders.')
      );
    }
  },
  {
    condition: ({ scope, isShared, force = false }, { getState }) => {
      const state = getState().overlayTemplates;
      const scopeKey = getOverlayTemplateFolderQueryKey(scope, isShared);
      if (
        state.activeFolderScopeKey === scopeKey &&
        (state.loadingFolders || state.loadingMoreFolders)
      )
        return false;
      if (force) return true;
      return (
        state.activeFolderScopeKey !== scopeKey &&
        state.loadedLibraryScopeKey !== scopeKey
      );
    }
  }
);

export const fetchNextOverlayTemplateFolders = createAsyncThunk<
  Awaited<ReturnType<typeof listOverlayTemplateFolders>>,
  {
    scope: OverlayTemplateApiScope;
    isShared?: boolean;
    scopeKey: string;
    nextUrl: string;
  },
  OverlayTemplateThunkConfig & {
    state: { overlayTemplates: OverlayTemplatesState };
  }
>(
  'overlayTemplates/fetchNextFolders',
  async ({ scope, isShared, nextUrl }, { rejectWithValue }) => {
    try {
      return await listOverlayTemplateFolders(scope, isShared, nextUrl);
    } catch (error: any) {
      return rejectWithValue(
        toRequestError(error, 'Failed to load more overlay folders.')
      );
    }
  },
  {
    condition: ({ scopeKey, nextUrl }, { getState }) => {
      const state = getState().overlayTemplates;
      return (
        state.activeFolderScopeKey === scopeKey &&
        state.loadedLibraryScopeKey === scopeKey &&
        state.folderNext === nextUrl &&
        !state.loadingFolders &&
        !state.loadingMoreFolders &&
        !state.loadedFolderCursors.includes(nextUrl)
      );
    }
  }
);

/** @deprecated Use fetchOverlayTemplates; folders are fetched separately. */
export const fetchOverlayTemplateLibrary = fetchOverlayTemplates;

export const fetchOverlayTemplateDetail = createAsyncThunk<
  OverlayTemplateDetailDto,
  { uuid: string; scope: OverlayTemplateApiScope },
  OverlayTemplateThunkConfig
>(
  'overlayTemplates/fetchDetail',
  async ({ uuid, scope }, { rejectWithValue }) => {
    try {
      return await getOverlayTemplate(uuid, scope);
    } catch (error: any) {
      return rejectWithValue(toRequestError(error, 'Failed to load overlay.'));
    }
  }
);

export const saveOverlayTemplate = createAsyncThunk<
  OverlayTemplateDetailDto,
  {
    uuid?: string;
    payload: OverlayTemplateWritePayload;
    scope: WritableOverlayTemplateApiScope;
  },
  OverlayTemplateThunkConfig
>(
  'overlayTemplates/save',
  async ({ uuid, payload, scope }, { rejectWithValue }) => {
    try {
      return uuid
        ? await updateOverlayTemplate(uuid, payload, scope)
        : await createOverlayTemplate(payload, scope);
    } catch (error: any) {
      return rejectWithValue(toRequestError(error, 'Failed to save overlay.'));
    }
  }
);

export const removeOverlayTemplate = createAsyncThunk<
  string,
  { uuid: string; scope: WritableOverlayTemplateApiScope },
  OverlayTemplateThunkConfig
>('overlayTemplates/delete', async ({ uuid, scope }, { rejectWithValue }) => {
  try {
    await deleteOverlayTemplate(uuid, scope);
    return uuid;
  } catch (error: any) {
    return rejectWithValue(toRequestError(error, 'Failed to delete overlay.'));
  }
});

export const addOverlayTemplateFolder = createAsyncThunk<
  OverlayTemplateFolderDto,
  { name: string; scope: WritableOverlayTemplateApiScope },
  OverlayTemplateThunkConfig
>(
  'overlayTemplates/addFolder',
  async ({ name, scope }, { rejectWithValue }) => {
    try {
      return await createOverlayTemplateFolder(name, scope);
    } catch (error: any) {
      return rejectWithValue(
        toRequestError(error, 'Failed to create overlay folder.')
      );
    }
  }
);

export const moveOverlayTemplateToFolder = createAsyncThunk<
  OverlayTemplateDetailDto,
  {
    uuid: string;
    folderUuid: string | null;
    scope: WritableOverlayTemplateApiScope;
  },
  OverlayTemplateThunkConfig
>(
  'overlayTemplates/moveToFolder',
  async ({ uuid, folderUuid, scope }, { rejectWithValue }) => {
    try {
      return await updateOverlayTemplate(
        uuid,
        { folder_uuid: folderUuid },
        scope
      );
    } catch (error: any) {
      return rejectWithValue(
        toRequestError(error, 'Failed to change the overlay folder.')
      );
    }
  }
);

export const renameOverlayTemplateFolder = createAsyncThunk<
  OverlayTemplateFolderDto,
  { uuid: string; name: string; scope: WritableOverlayTemplateApiScope },
  OverlayTemplateThunkConfig
>(
  'overlayTemplates/renameFolder',
  async ({ uuid, name, scope }, { rejectWithValue }) => {
    try {
      return await updateOverlayTemplateFolder(uuid, name, scope);
    } catch (error: any) {
      return rejectWithValue(
        toRequestError(error, 'Failed to rename overlay folder.')
      );
    }
  }
);

export const removeOverlayTemplateFolder = createAsyncThunk<
  string,
  { uuid: string; scope: WritableOverlayTemplateApiScope },
  OverlayTemplateThunkConfig
>(
  'overlayTemplates/deleteFolder',
  async ({ uuid, scope }, { rejectWithValue }) => {
    try {
      await deleteOverlayTemplateFolder(uuid, scope);
      return uuid;
    } catch (error: any) {
      return rejectWithValue(
        toRequestError(error, 'Failed to delete overlay folder.')
      );
    }
  }
);

export const uploadOverlayTemplatePreview = createAsyncThunk<
  string,
  {
    file: File;
    locationUuid?: string;
    onProgress?: (percent: number) => void;
    signal?: AbortSignal;
  },
  OverlayTemplateThunkConfig
>('overlayTemplates/uploadPreview', async (options, { rejectWithValue }) => {
  try {
    const upload = await uploadOverlayTemplatePreviewFile(options);
    return upload.uploaded_object_data?.uuid ?? upload.uuid;
  } catch (error: any) {
    return rejectWithValue(
      toRequestError(error, 'Failed to upload overlay preview.')
    );
  }
});

const overlayTemplatesSlice = createSlice({
  name: 'overlayTemplates',
  initialState,
  reducers: {
    clearActiveTemplate: (state) => {
      state.activeTemplate = null;
      state.loadingTemplate = false;
      state.templateRequestId = null;
      state.templateError = null;
      state.fieldErrors = {};
    },
    clearOverlayTemplateError: (state) => {
      state.error = null;
      state.loadMoreError = null;
      state.folderError = null;
      state.folderLoadMoreError = null;
      state.templateError = null;
      state.fieldErrors = {};
    },
    setActiveTemplate: (
      state,
      action: PayloadAction<OverlayTemplateDetailDto | null>
    ) => {
      state.activeTemplate = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOverlayTemplates.pending, (state, action) => {
        const queryKey = getOverlayTemplateQueryKey(action.meta.arg);
        state.loadingLibrary = true;
        state.libraryRequestId = action.meta.requestId;
        state.activeLibraryQueryKey = queryKey;
        state.templates = [];
        state.count = 0;
        // The quota belongs to the library, not to the query. Clearing it on
        // every search, sort or folder change unblocked the create button for
        // the length of each request, in a library that was full both before
        // and after.
        const scopeKey = getOverlayTemplateScopeKey(action.meta.arg.scope);
        if (state.quotaScopeKey !== scopeKey) {
          state.quota = null;
          state.quotaScopeKey = scopeKey;
        }
        state.next = null;
        state.previous = null;
        state.loadingMoreTemplates = false;
        state.loadMoreRequestId = null;
        state.loadingMoreCursor = null;
        state.loadedTemplateCursors = [];
        state.loadMoreError = null;
        state.error = null;
      })
      .addCase(fetchOverlayTemplates.fulfilled, (state, action) => {
        const queryKey = getOverlayTemplateQueryKey(action.meta.arg);
        if (
          state.libraryRequestId !== action.meta.requestId ||
          state.activeLibraryQueryKey !== queryKey
        )
          return;
        state.loadingLibrary = false;
        state.libraryRequestId = null;
        state.templates = action.payload.results;
        state.count = action.payload.count ?? action.payload.results.length;
        state.quota = action.payload.quota ?? null;
        state.quotaScopeKey = getOverlayTemplateScopeKey(action.meta.arg.scope);
        state.next = action.payload.next;
        state.previous = action.payload.previous;
      })
      .addCase(fetchOverlayTemplates.rejected, (state, action) => {
        const queryKey = getOverlayTemplateQueryKey(action.meta.arg);
        if (
          state.libraryRequestId !== action.meta.requestId ||
          state.activeLibraryQueryKey !== queryKey
        )
          return;
        state.loadingLibrary = false;
        state.libraryRequestId = null;
        state.error = action.payload?.message ?? 'Failed to load overlays.';
      })
      .addCase(fetchNextOverlayTemplates.pending, (state, action) => {
        if (
          state.activeLibraryQueryKey !== action.meta.arg.queryKey ||
          state.next !== action.meta.arg.nextUrl
        )
          return;
        state.loadingMoreTemplates = true;
        state.loadMoreRequestId = action.meta.requestId;
        state.loadingMoreCursor = action.meta.arg.nextUrl;
        state.loadMoreError = null;
      })
      .addCase(fetchNextOverlayTemplates.fulfilled, (state, action) => {
        if (
          state.loadMoreRequestId !== action.meta.requestId ||
          state.activeLibraryQueryKey !== action.meta.arg.queryKey ||
          state.loadingMoreCursor !== action.meta.arg.nextUrl
        )
          return;
        const knownUuids = new Set(state.templates.map(({ uuid }) => uuid));
        let appendedCount = 0;
        for (const template of action.payload.results) {
          if (knownUuids.has(template.uuid)) continue;
          knownUuids.add(template.uuid);
          state.templates.push(template);
          appendedCount += 1;
        }
        state.loadedTemplateCursors.push(action.meta.arg.nextUrl);
        state.count = action.payload.count ?? state.count + appendedCount;
        state.quota = action.payload.quota ?? state.quota;
        state.previous = action.payload.previous;
        state.next =
          action.payload.next === action.meta.arg.nextUrl ||
          (action.payload.next !== null &&
            state.loadedTemplateCursors.includes(action.payload.next))
            ? null
            : action.payload.next;
        state.loadingMoreTemplates = false;
        state.loadMoreRequestId = null;
        state.loadingMoreCursor = null;
        state.loadMoreError = null;
      })
      .addCase(fetchNextOverlayTemplates.rejected, (state, action) => {
        if (
          state.loadMoreRequestId !== action.meta.requestId ||
          state.activeLibraryQueryKey !== action.meta.arg.queryKey ||
          state.loadingMoreCursor !== action.meta.arg.nextUrl
        )
          return;
        state.loadingMoreTemplates = false;
        state.loadMoreRequestId = null;
        state.loadingMoreCursor = null;
        state.loadMoreError =
          action.payload?.message ?? 'Failed to load more overlays.';
      })
      .addCase(fetchOverlayTemplateFolders.pending, (state, action) => {
        const scopeKey = getOverlayTemplateFolderQueryKey(
          action.meta.arg.scope,
          action.meta.arg.isShared
        );
        state.loadingFolders = true;
        state.folderRequestId = action.meta.requestId;
        state.activeFolderScopeKey = scopeKey;
        state.loadedLibraryScopeKey = null;
        state.folders = [];
        state.folderNext = null;
        state.loadingMoreFolders = false;
        state.folderLoadMoreRequestId = null;
        state.loadingFolderCursor = null;
        state.loadedFolderCursors = [];
        state.folderError = null;
        state.folderLoadMoreError = null;
      })
      .addCase(fetchOverlayTemplateFolders.fulfilled, (state, action) => {
        const scopeKey = getOverlayTemplateFolderQueryKey(
          action.meta.arg.scope,
          action.meta.arg.isShared
        );
        if (
          state.folderRequestId !== action.meta.requestId ||
          state.activeFolderScopeKey !== scopeKey
        )
          return;
        state.loadingFolders = false;
        state.folderRequestId = null;
        state.loadedLibraryScopeKey = scopeKey;
        state.folders = action.payload.results;
        state.folderNext = action.payload.next;
      })
      .addCase(fetchOverlayTemplateFolders.rejected, (state, action) => {
        const scopeKey = getOverlayTemplateFolderQueryKey(
          action.meta.arg.scope,
          action.meta.arg.isShared
        );
        if (
          state.folderRequestId !== action.meta.requestId ||
          state.activeFolderScopeKey !== scopeKey
        )
          return;
        state.loadingFolders = false;
        state.folderRequestId = null;
        state.folderError =
          action.payload?.message ?? 'Failed to load overlay folders.';
      })
      .addCase(fetchNextOverlayTemplateFolders.pending, (state, action) => {
        if (
          state.activeFolderScopeKey !== action.meta.arg.scopeKey ||
          state.loadedLibraryScopeKey !== action.meta.arg.scopeKey ||
          state.folderNext !== action.meta.arg.nextUrl
        )
          return;
        state.loadingMoreFolders = true;
        state.folderLoadMoreRequestId = action.meta.requestId;
        state.loadingFolderCursor = action.meta.arg.nextUrl;
        state.folderLoadMoreError = null;
      })
      .addCase(fetchNextOverlayTemplateFolders.fulfilled, (state, action) => {
        if (
          state.folderLoadMoreRequestId !== action.meta.requestId ||
          state.activeFolderScopeKey !== action.meta.arg.scopeKey ||
          state.loadedLibraryScopeKey !== action.meta.arg.scopeKey ||
          state.loadingFolderCursor !== action.meta.arg.nextUrl
        )
          return;
        const knownUuids = new Set(state.folders.map(({ uuid }) => uuid));
        for (const folder of action.payload.results) {
          if (knownUuids.has(folder.uuid)) continue;
          knownUuids.add(folder.uuid);
          state.folders.push(folder);
        }
        state.folders = sortFoldersByName(state.folders);
        state.loadedFolderCursors.push(action.meta.arg.nextUrl);
        state.folderNext =
          action.payload.next === action.meta.arg.nextUrl ||
          (action.payload.next !== null &&
            state.loadedFolderCursors.includes(action.payload.next))
            ? null
            : action.payload.next;
        state.loadingMoreFolders = false;
        state.folderLoadMoreRequestId = null;
        state.loadingFolderCursor = null;
        state.folderLoadMoreError = null;
      })
      .addCase(fetchNextOverlayTemplateFolders.rejected, (state, action) => {
        if (
          state.folderLoadMoreRequestId !== action.meta.requestId ||
          state.activeFolderScopeKey !== action.meta.arg.scopeKey ||
          state.loadedLibraryScopeKey !== action.meta.arg.scopeKey ||
          state.loadingFolderCursor !== action.meta.arg.nextUrl
        )
          return;
        state.loadingMoreFolders = false;
        state.folderLoadMoreRequestId = null;
        state.loadingFolderCursor = null;
        state.folderLoadMoreError =
          action.payload?.message ?? 'Failed to load more overlay folders.';
      })
      .addCase(fetchOverlayTemplateDetail.pending, (state, action) => {
        state.loadingTemplate = true;
        state.templateRequestId = action.meta.requestId;
        state.error = null;
        state.templateError = null;
      })
      .addCase(fetchOverlayTemplateDetail.fulfilled, (state, action) => {
        // Open A, then B before A answers: A's late success would otherwise
        // install A over B and clear the error B just recorded, leaving the
        // editor waiting on a template nothing is fetching any more.
        if (state.templateRequestId !== action.meta.requestId) return;
        state.loadingTemplate = false;
        state.templateRequestId = null;
        state.activeTemplate = action.payload;
        state.templateError = null;
      })
      .addCase(fetchOverlayTemplateDetail.rejected, (state, action) => {
        if (state.templateRequestId !== action.meta.requestId) return;
        state.loadingTemplate = false;
        state.templateRequestId = null;
        // Only `templateError`: the library renders `error`, and a failure
        // that belongs to the editor has no business being read out over the
        // list the user returns to.
        state.templateError =
          action.payload?.message ?? 'Failed to load overlay.';
      })
      .addCase(saveOverlayTemplate.pending, (state) => {
        state.savingTemplate = true;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(saveOverlayTemplate.fulfilled, (state, action) => {
        state.savingTemplate = false;
        state.activeTemplate = action.payload;
        upsertTemplate(state, action.payload);
      })
      .addCase(saveOverlayTemplate.rejected, (state, action) => {
        state.savingTemplate = false;
        state.error = action.payload?.message ?? 'Failed to save overlay.';
        state.fieldErrors = action.payload?.fieldErrors ?? {};
      })
      .addCase(removeOverlayTemplate.pending, (state, action) => {
        state.deletingTemplateUuid = action.meta.arg.uuid;
        state.error = null;
      })
      .addCase(removeOverlayTemplate.fulfilled, (state, action) => {
        state.deletingTemplateUuid = null;
        state.templates = state.templates.filter(
          (template) => template.uuid !== action.payload
        );
        state.count = Math.max(0, state.count - 1);
        releaseQuotaSlots(state, 1);
        if (state.activeTemplate?.uuid === action.payload) {
          state.activeTemplate = null;
        }
      })
      .addCase(removeOverlayTemplate.rejected, (state, action) => {
        state.deletingTemplateUuid = null;
        state.error = action.payload?.message ?? 'Failed to delete overlay.';
      })
      .addCase(moveOverlayTemplateToFolder.pending, (state, action) => {
        state.movingTemplateUuid = action.meta.arg.uuid;
        state.error = null;
        state.fieldErrors = {};
      })
      .addCase(moveOverlayTemplateToFolder.fulfilled, (state, action) => {
        state.movingTemplateUuid = null;
        upsertTemplate(state, action.payload);
        if (state.activeTemplate?.uuid === action.payload.uuid) {
          state.activeTemplate = action.payload;
        }
      })
      .addCase(moveOverlayTemplateToFolder.rejected, (state, action) => {
        state.movingTemplateUuid = null;
        state.error =
          action.payload?.message ?? 'Failed to change the overlay folder.';
        state.fieldErrors = action.payload?.fieldErrors ?? {};
      })
      .addCase(addOverlayTemplateFolder.fulfilled, (state, action) => {
        state.mutatingFolder = false;
        state.folders = sortFoldersByName([
          ...state.folders.filter(
            (folder) => folder.uuid !== action.payload.uuid
          ),
          action.payload
        ]);
      })
      .addCase(renameOverlayTemplateFolder.fulfilled, (state, action) => {
        state.mutatingFolder = false;
        const index = state.folders.findIndex(
          (folder) => folder.uuid === action.payload.uuid
        );
        if (index >= 0) state.folders[index] = action.payload;
        state.folders = sortFoldersByName(state.folders);
      })
      .addCase(removeOverlayTemplateFolder.fulfilled, (state, action) => {
        state.mutatingFolder = false;
        state.folders = state.folders.filter(
          (folder) => folder.uuid !== action.payload
        );
        const templateCountBeforeDelete = state.templates.length;
        state.templates = state.templates.filter(
          (template) => template.folder_uuid !== action.payload
        );
        // Only the templates this cursor page happened to hold can be
        // counted here, so the quota is deliberately left alone: guessing
        // low would keep a library that has room reading as full. The page
        // refetches the list after a folder delete, and the server answers
        // with the real quota.
        state.count = Math.max(
          0,
          state.count - (templateCountBeforeDelete - state.templates.length)
        );
        if (state.activeTemplate?.folder_uuid === action.payload) {
          state.activeTemplate = null;
        }
      })
      .addCase(uploadOverlayTemplatePreview.pending, (state) => {
        state.uploadingPreview = true;
        state.error = null;
      })
      .addCase(uploadOverlayTemplatePreview.fulfilled, (state) => {
        state.uploadingPreview = false;
      })
      .addCase(uploadOverlayTemplatePreview.rejected, (state, action) => {
        state.uploadingPreview = false;
        state.error =
          action.payload?.message ?? 'Failed to upload overlay preview.';
      })
      .addMatcher(
        (action) =>
          [
            addOverlayTemplateFolder.pending.type,
            renameOverlayTemplateFolder.pending.type,
            removeOverlayTemplateFolder.pending.type
          ].includes(action.type),
        (state) => {
          state.mutatingFolder = true;
          state.error = null;
          state.fieldErrors = {};
        }
      )
      .addMatcher(
        (action) =>
          [
            addOverlayTemplateFolder.rejected.type,
            renameOverlayTemplateFolder.rejected.type,
            removeOverlayTemplateFolder.rejected.type
          ].includes(action.type),
        (state, action: any) => {
          state.mutatingFolder = false;
          state.error =
            action.payload?.message ?? 'Failed to update overlay folders.';
          state.fieldErrors = action.payload?.fieldErrors ?? {};
        }
      );
  }
});

/**
 * Gives the library back the slots a delete freed, so the counter and the
 * create button answer straight away rather than waiting for the next list.
 *
 * Only deletions are tracked here: a save leaves the library through a full
 * page navigation, which comes back with a fresh quota from the server.
 */
const releaseQuotaSlots = (state: OverlayTemplatesState, slots: number) => {
  const quota = state.quota;
  if (!quota || quota.limit === null || quota.used === null || slots <= 0)
    return;

  const used = Math.max(0, quota.used - slots);
  state.quota = {
    ...quota,
    used,
    remaining: Math.max(0, quota.limit - used),
    is_limit_reached: used >= quota.limit
  };
};

const upsertTemplate = (
  state: OverlayTemplatesState,
  template: OverlayTemplateSummaryDto
) => {
  const index = state.templates.findIndex(
    (item) => item.uuid === template.uuid
  );
  if (index >= 0) {
    state.templates[index] = template;
  } else {
    state.templates = [template, ...state.templates];
    state.count += 1;
  }
};

export const overlayTemplateActions = overlayTemplatesSlice.actions;
export default overlayTemplatesSlice.reducer;
